import { Link } from "react-router-dom";
import {
  ArrowRight, Phone, ShieldCheck, HardHat, PackageCheck, Users2, Timer, ClipboardCheck,
  Landmark, CheckCircle2, MapPin,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  SiteLayout, SectionTag, WhatsAppIcon, waLink, useLeadModal, PHONE_PRIMARY, PHONE_PRIMARY_TEL,
  TRUST_INDICATORS, TRUST_SEGMENTS, SERVICES, INDUSTRIES, DISTRICTS, MAP_EMBED_SRC, useSEO,
} from "./shared";

const HERO = "https://images.unsplash.com/photo-1772034736198-b194e4d72a1e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const TECH = "https://images.unsplash.com/photo-1748256086767-8974ee677f77?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const WHY_US = [
  { icon: HardHat, title: "Professional Installation" },
  { icon: PackageCheck, title: "Quality Materials" },
  { icon: Users2, title: "Skilled Technical Team" },
  { icon: Timer, title: "Timely Project Execution" },
  { icon: ClipboardCheck, title: "Testing & Commissioning" },
  { icon: ShieldCheck, title: "Reliable Maintenance Support" },
  { icon: CheckCircle2, title: "Complete Fire Protection Solutions" },
  { icon: Landmark, title: "Government & Private Sector Capability" },
];

export default function Home() {
  const { openSiteVisit } = useLeadModal();
  useSEO(
    "Fire Fighting System Installation in Tripura | SecureFlame Fire Systems",
    "SecureFlame Fire Systems Pvt. Ltd. provides fire alarm, hydrant, sprinkler, fire pump, extinguisher, testing, commissioning and AMC services across Tripura. Request a site visit today."
  );

  return (
    <SiteLayout>
      {/* HERO */}
      <section className="relative bg-[#081A33] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="Fire protection engineer inspecting a fire pump room in Tripura" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#081A33] via-[#081A33]/90 to-[#081A33]/50" />
          <div className="absolute inset-0 grain" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32">
          <SectionTag light>Fire Protection &amp; ELV Engineering Across Tripura</SectionTag>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-[3.5rem] tracking-tight leading-[1.05] max-w-4xl animate-fade-up">
            Complete Fire Protection Solutions <span className="text-primary">Across Tripura</span>
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-gold font-medium tracking-wide animate-fade-up" style={{ animationDelay: "0.1s" }}>
            Design • Supply • Installation • Testing • Commissioning • AMC
          </p>
          <div className="mt-7 flex flex-wrap gap-2 max-w-3xl animate-fade-up" style={{ animationDelay: "0.15s" }}>
            {SERVICES.map((s) => (
              <span key={s.slug} className="text-xs font-medium px-3 py-1.5 rounded-full border border-white/15 bg-white/5 text-slate-200">{s.short}</span>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-3.5 animate-fade-up" style={{ animationDelay: "0.2s" }}>
            <Button onClick={() => openSiteVisit()} size="lg" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold gap-2" data-testid="hero-site-visit-btn">REQUEST FREE SITE VISIT <ArrowRight size={18} /></Button>
            <a href={`tel:${PHONE_PRIMARY_TEL}`}><Button size="lg" variant="outline" className="rounded-sm border-white/25 text-white bg-transparent hover:bg-white/10 font-semibold gap-2" data-testid="hero-call-btn"><Phone size={18} /> CALL NOW</Button></a>
            <a href={waLink()} target="_blank" rel="noreferrer"><Button size="lg" variant="outline" className="rounded-sm border-white/25 text-white bg-transparent hover:bg-white/10 font-semibold gap-2" data-testid="hero-whatsapp-btn"><WhatsAppIcon size={18} /> WHATSAPP US</Button></a>
          </div>
          <p className="mt-8 text-sm text-slate-400 animate-fade-up" style={{ animationDelay: "0.25s" }}>
            {PHONE_PRIMARY} &nbsp;·&nbsp; +91 82402 06961 &nbsp;·&nbsp; secureflame2026@gmail.com
          </p>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="bg-[#FFFDF8] border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
          {TRUST_SEGMENTS.map((s, i) => (
            <span key={s} className="text-sm font-semibold text-secondary tracking-wide flex items-center gap-2" data-testid={`trust-segment-${i}`}>
              {i > 0 && <span className="text-gold">•</span>}{s}
            </span>
          ))}
        </div>
        <div className="max-w-7xl mx-auto px-6 pb-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2.5" data-testid="trust-strip">
          {TRUST_INDICATORS.map((t, i) => (
            <span key={t} className="flex items-center gap-x-6">
              {i > 0 && <span className="hidden sm:block w-px h-3.5 bg-gold/50" aria-hidden="true" />}
              <span className="text-xs font-semibold text-secondary/90 tracking-wide flex items-center gap-1.5" data-testid={`trust-indicator-${i}`}>
                <CheckCircle2 size={13} className="text-gold shrink-0" /> {t.toUpperCase()}
              </span>
            </span>
          ))}
        </div>
      </section>

      {/* CORE SERVICES */}
      <section className="max-w-7xl mx-auto px-6 py-20 md:py-24">
        <div className="max-w-2xl mb-12">
          <SectionTag>Our Services</SectionTag>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-secondary">Engineering every layer of fire protection.</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s) => (
            <div key={s.slug} className="group p-7 rounded-sm border border-border bg-card hover:border-primary/40 hover:shadow-lg transition-all duration-200" data-testid={`service-card-${s.slug}`}>
              <s.icon className="text-primary mb-4" size={30} />
              <h3 className="font-heading font-bold text-base text-secondary">{s.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link to="/services"><Button size="lg" variant="outline" className="rounded-sm border-secondary/20 text-secondary hover:bg-secondary hover:text-white gap-2" data-testid="services-view-all-btn">View All Services <ArrowRight size={16} /></Button></Link>
        </div>
      </section>

      {/* FREE SITE VISIT CTA */}
      <section className="relative blueprint-bg text-white">
        <div className="relative max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionTag light>Free Assessment</SectionTag>
            <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">Is Your Building Fire-Safety Ready?</h2>
            <p className="mt-3 text-xl text-gold font-semibold">Book a Free Initial Site Visit</p>
            <p className="mt-4 text-slate-300 max-w-lg">We assess your existing fire protection arrangements and recommend suitable systems, upgrades or maintenance requirements.</p>
            <div className="mt-8 flex flex-wrap gap-3.5">
              <Link to="/contact?type=site_visit"><Button size="lg" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold" data-testid="sitevisit-book-btn">BOOK SITE VISIT</Button></Link>
              <a href={waLink()} target="_blank" rel="noreferrer"><Button size="lg" variant="outline" className="rounded-sm border-white/25 text-white bg-transparent hover:bg-white/10 gap-2 font-semibold" data-testid="sitevisit-whatsapp-btn"><WhatsAppIcon size={18} /> WHATSAPP SECUREFLAME</Button></a>
              <a href={`tel:${PHONE_PRIMARY_TEL}`}><Button size="lg" variant="outline" className="rounded-sm border-white/25 text-white bg-transparent hover:bg-white/10 gap-2 font-semibold" data-testid="sitevisit-call-btn"><Phone size={18} /> CALL {PHONE_PRIMARY}</Button></a>
            </div>
          </div>
          <img src={TECH} alt="Fire safety technician assessing a site in Tripura" className="w-full h-[360px] object-cover rounded-sm border border-white/10" />
        </div>
      </section>

      {/* OUR WORK PREVIEW */}
      <section className="max-w-7xl mx-auto px-6 py-20 md:py-24">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div className="max-w-xl">
            <SectionTag>Our Work</SectionTag>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-secondary">Real installations. Professional execution.</h2>
          </div>
          <Link to="/our-work"><Button variant="outline" className="rounded-sm border-secondary/20 text-secondary hover:bg-secondary hover:text-white gap-2" data-testid="ourwork-viewall-btn">View Full Gallery <ArrowRight size={16} /></Button></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1, 4, 8, 2, 12, 3, 9, 13].map((n) => (
            <div key={n} className="aspect-square overflow-hidden rounded-sm border border-border bg-muted group">
              <img src={`/installs/site-${n}.jpg`} alt={`SecureFlame fire protection installation ${n}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
            </div>
          ))}
        </div>
      </section>

      {/* INDUSTRIES PREVIEW */}
      <section className="bg-muted">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-24">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div className="max-w-xl">
              <SectionTag>Industries We Serve</SectionTag>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-secondary">Trusted across every sector.</h2>
            </div>
            <Link to="/industries"><Button variant="outline" className="rounded-sm border-secondary/20 text-secondary hover:bg-secondary hover:text-white gap-2" data-testid="industries-viewall-btn">View All Industries <ArrowRight size={16} /></Button></Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {INDUSTRIES.slice(0, 10).map((ind) => (
              <div key={ind.title} className="bg-card border border-border rounded-sm p-5 text-center hover:border-primary/40 hover:shadow-md transition-all">
                <ind.icon className="text-primary mx-auto mb-3" size={26} />
                <p className="text-sm font-semibold text-secondary">{ind.title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FIRE NOC SUMMARY */}
      <section className="max-w-7xl mx-auto px-6 py-20 md:py-24 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <SectionTag>Fire Safety &amp; NOC Support</SectionTag>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-secondary">We help you get compliance-ready.</h2>
          <p className="mt-4 text-muted-foreground max-w-2xl">SecureFlame assists clients with site survey, fire protection system planning, installation, testing &amp; commissioning, technical documentation and rectification of deficiencies — supporting your Fire Safety Certificate / NOC application process.</p>
          <p className="mt-3 text-sm text-muted-foreground max-w-2xl italic">Statutory Fire Safety Certificate / NOC approval is issued solely by the relevant government authority. SecureFlame does not issue, approve or guarantee a Fire NOC.</p>
          <Link to="/fire-noc-support"><Button size="lg" className="mt-7 rounded-sm bg-primary hover:bg-primary/90 gap-2" data-testid="noc-summary-btn">REQUEST A FIRE SAFETY ASSESSMENT <ArrowRight size={16} /></Button></Link>
        </div>
        <div className="lg:col-span-5 bg-secondary text-white rounded-sm p-8 blueprint-bg">
          <ul className="space-y-3.5 text-sm">
            {["Site survey & documentation", "Fire system planning & drawings", "Installation, testing & commissioning", "Rectification of deficiencies", "AMC & periodic maintenance"].map((t) => (
              <li key={t} className="flex items-start gap-3"><CheckCircle2 size={17} className="text-gold shrink-0 mt-0.5" /> {t}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="bg-secondary text-white">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-24">
          <div className="max-w-2xl mb-12">
            <SectionTag light>Why Choose SecureFlame</SectionTag>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">Built on engineering discipline.</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            {WHY_US.map((w) => (
              <div key={w.title} className="text-center">
                <w.icon className="text-gold mx-auto mb-3" size={30} />
                <p className="text-sm font-medium text-slate-200">{w.title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE COVERAGE */}
      <section className="max-w-7xl mx-auto px-6 py-20 md:py-24">
        <div className="max-w-2xl mb-10">
          <SectionTag>Service Coverage</SectionTag>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-secondary">Fire Protection Services Across Tripura.</h2>
        </div>
        <div className="flex flex-wrap gap-3">
          {DISTRICTS.map((d) => (
            <span key={d} className="flex items-center gap-2 text-sm font-medium text-secondary bg-muted border border-border px-4 py-2.5 rounded-sm" data-testid={`district-${d.toLowerCase().replace(/\s+/g, "-")}`}>
              <MapPin size={14} className="text-primary" /> {d}
            </span>
          ))}
        </div>
      </section>

      {/* CONTACT TEASER + MAP */}
      <section className="bg-muted">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-24 grid lg:grid-cols-2 gap-10">
          <div>
            <SectionTag>Get In Touch</SectionTag>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-secondary">Let's discuss your fire safety requirements.</h2>
            <p className="mt-4 text-muted-foreground max-w-md">Call, WhatsApp, or send us a message — our team will respond and arrange a free site visit where required.</p>
            <div className="mt-8 flex flex-wrap gap-3.5">
              <Link to="/contact"><Button size="lg" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold" data-testid="contact-teaser-btn">GET A QUOTE</Button></Link>
              <a href={`tel:${PHONE_PRIMARY_TEL}`}><Button size="lg" variant="outline" className="rounded-sm border-secondary/20 text-secondary hover:bg-secondary hover:text-white gap-2 font-semibold"><Phone size={16} /> {PHONE_PRIMARY}</Button></a>
            </div>
          </div>
          <div className="rounded-sm overflow-hidden border border-border h-64 lg:h-auto">
            <iframe title="SecureFlame Fire Systems location map" src={MAP_EMBED_SRC} className="w-full h-full min-h-[260px]" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
